  <template>
  <div class="customers-page mt-3">
    <div class="row">
      <div class="col-md-3">
        <h4>Klanten</h4>
        <ul class="list-group">
          <li v-for="customer in customers" :key="customer.id" 
              class="list-group-item customer-item" 
              :class="{ 'active custom-active': selectedCustomer && selectedCustomer.id === customer.id }"
              @click="selectCustomer(customer)">
            {{ customer.name }}
          </li>
        </ul>
        <!-- <button class="btn btn-primary mt-3 mb-3 ms-auto" @click="createNewCustomer">Nieuwe klant</button> -->
      </div>
      <div class="col-md-9">
        <h4>{{ selectedCustomer ? 'Bewerken' : 'Nieuwe klant' }}</h4>
        <form @submit.prevent="saveCustomer" class="mb-4">
          <div class="mb-3">
            <label for="name" class="form-label">Naam</label>
            <input type="text" class="form-control" id="name" v-model="editingCustomer.name" required>
          </div>
          <div class="mb-3">
            <label for="username" class="form-label">Gebruikersnaam</label>
            <input type="text" class="form-control" id="username" v-model="editingCustomer.username" required>
          </div>
          <div class="mb-3">
            <label for="password" class="form-label">Wachtwoord</label>
            <PasswordInput 
              :value="editingCustomer.password" 
              @passwordChanged="updatePassword"
            />
            <small class="form-text text-muted" v-if="!isCreating">Laat leeg om het wachtwoord niet te wijzigen</small>
          </div>
          <div class="mb-3">
            <label for="customer_code" class="form-label">Customer Code</label>
            <input type="text" class="form-control" id="customer_code" v-model="editingCustomer.customer_code" :required="isCreating">
            <small class="form-text text-muted">Unieke identificatiecode voor de klant</small>
          </div>
          <div class="mb-3">
            <label for="organization_code" class="form-label">Organization Code</label>
            <input type="text" class="form-control" id="organization_code" v-model="editingCustomer.organization_code" :required="isCreating">
            <small class="form-text text-muted">Organisatiecode van de klant</small>
          </div>
          <div class="mb-3">
            <label for="portal_url" class="form-label">Klantportaal URL</label>
            <input type="url" class="form-control" id="portal_url" v-model="editingCustomer.portal_url" placeholder="https://...">
            <small class="form-text text-muted">Link naar het klantportaal waar de klant orders kan plaatsen</small>
            <div v-if="editingCustomer.portal_url" class="mt-2">
              <a v-if="isValidUrl(editingCustomer.portal_url)" 
                 :href="editingCustomer.portal_url" 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 class="btn btn-sm btn-outline-primary">
                <i class="bi bi-box-arrow-up-right me-1"></i>Klantportaal openen
              </a>
              <div v-else class="text-warning small">
                <i class="bi bi-exclamation-triangle me-1"></i>Ongeldige URL
              </div>
            </div>
          </div>
          <div class="mt-4 d-flex justify-content-end">
            <button type="submit" class="btn btn-primary">Opslaan</button>
            <button type="button" class="btn btn-danger ms-2" @click="deleteCustomer" v-if="selectedCustomer">Verwijderen</button>
          </div>
          <!-- E-mails met Collapse -->
          <div class="mt-5 mb-4">
            <button type="button" 
                    class="btn btn-link text-start d-flex justify-content-between align-items-center w-100 p-0 border-0"
                    @click="toggleEmailCollapse"
                    :aria-expanded="!isEmailCollapsed"
                    aria-controls="emailCollapse"
                    :aria-label="`${isEmailCollapsed ? 'Expand' : 'Collapse'} email section`">
              <h4 class="mb-0">E-mail <span class="badge rounded-pill bg-secondary badge-sm" v-if="totalEmailCount > 0">{{ totalEmailCount }}</span></h4>
              <span class="btn btn-sm btn-secondary">
                <i class="bi" :class="isEmailCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'"></i>
              </span>
            </button>
            <div class="collapse" :class="{ 'show': !isEmailCollapsed }" id="emailCollapse">
              <div class="mt-3">
                <EmailManager 
                  v-model="editingCustomer.email" 
                  @save="handleEmailSave"
                  @delete="handleEmailDelete"
                  @error="handleEmailError"
                />
              </div>
            </div>
          </div>
          <!-- Debiteurnummer met Collapse -->
          <div class="mb-4">
            <button type="button" 
                    class="btn btn-link text-start d-flex justify-content-between align-items-center w-100 p-0 border-0"
                    @click="toggleDebtorCollapse"
                    :aria-expanded="!isDebtorCollapsed"
                    aria-controls="debtorCollapse"
                    :aria-label="`${isDebtorCollapsed ? 'Expand' : 'Collapse'} debtor section`">
              <h4 class="mb-0">Debiteur <span class="badge rounded-pill bg-secondary badge-sm" v-if="customerDebtors.length > 0">{{ customerDebtors.length }}</span></h4>
              <span class="btn btn-sm btn-secondary">
                <i class="bi" :class="isDebtorCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'"></i>
              </span>
            </button>
            <div class="collapse" :class="{ 'show': !isDebtorCollapsed }" id="debtorCollapse">
              <div class="mt-3">
                <div class="row mb-2">
                  <div class="col-12">
                    <div class="table-responsive">
                      <table class="table table-hover">
                        <thead>
                          <tr>
                            <th>Naam</th>
                            <th>Address ID</th>
                            <th>Organisation Code</th>
                            <th>Debtor Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="debtor in customerDebtors" :key="debtor.id" 
                              @click="navigateToDebtor(debtor.id)"
                              class="cursor-pointer">
                            <td>{{ debtor.name }}</td>
                            <td>{{ debtor.address_id }}</td>
                            <td>{{ debtor.organisation_code }}</td>
                            <td>{{ debtor.debtor_code }}</td>
                          </tr>
                          <tr v-if="customerDebtors.length === 0" class="text-muted">
                            <td colspan="4" class="text-center">Geen debiteuren gevonden</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                <!-- Debiteur toevoegen knop en beschrijving op één regel -->
                <div class="row mt-3">
                  <div class="col-12">
                    <div class="d-flex justify-content-between align-items-center">
                      <!-- Beschrijving als notitie (links, max 50%) -->
                      <div class="description-container" style="width: 50%; overflow: hidden; text-overflow: ellipsis;">
                        <span 
                          v-if="!isEditingDescription" 
                          @click="startEditingDescription" 
                          class="description-text text-muted small" 
                          style="cursor: pointer;"
                        >
                          {{ editingCustomer.debtor_codes_descr || 'Het default debiteurnummer dat wordt gebruikt voor het insturen van orders. (Bij één debiteurnummer maakt het niet uit welke sleutel wordt gebruikt.)' }}
                          <i class="bi bi-pencil-fill ms-1 text-muted small"></i>
                        </span>
                        <div v-else class="input-group">
                          <input 
                            type="text" 
                            class="form-control form-control-sm" 
                            v-model="editingCustomer.debtor_codes_descr" 
                            @keyup.esc="cancelEditingDescription"
                          />
                          <button class="btn btn-sm btn-outline-secondary" type="button" @click="stopEditingDescription">
                            <i class="bi bi-check"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary" type="button" @click="cancelEditingDescription">
                            <i class="bi bi-x"></i>
                          </button>
                        </div>
                      </div>
                      
                      <!-- Debiteur toevoegen knop (rechts) -->
                      <button type="button" class="btn btn-info ms-2" @click="addNewDebtor">
                        <i class="bi bi-plus-circle me-1"></i> Debiteur toevoegen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal dialoog is verplaatst naar de EmailManager component -->
  </div>
</template>

<script>
import ServiceFactory from '@/services/ServiceFactory';
import PasswordInput from '@/components/PasswordInput.vue';
import EmailManager from '../components/EmailManager.vue';
export default {
  name: 'CustomersPage',
  components: {
    PasswordInput,
    EmailManager
  },
  computed: {
    totalEmailCount() {
      if (!this.editingCustomer.email) return 0;
      
      let count = 0;
      for (const category of this.emailCategories) {
        if (Array.isArray(this.editingCustomer.email[category])) {
          count += this.editingCustomer.email[category].length;
        }
      }
      return count;
    }
  },
  watch: {
    // Luister naar wijzigingen in de route query parameters
    '$route.query': {
      async handler() {
        // Selecteer klant op basis van URL query parameters (indien aanwezig)
        await this.checkAndSelectCustomerFromUrl();
      },
      immediate: true
    }
  },
  data() {
    return {
      customers: [],
      selectedCustomer: null,
      editingCustomer: {},
      customerDebtors: [],
      isCreating: true,
      isEditingDescription: false,
      originalDescriptionText: '',
      isEmailCollapsed: localStorage.getItem('isEmailCollapsed') === 'false' ? false : true,
      isDebtorCollapsed: localStorage.getItem('isDebtorCollapsed') === 'false' ? false : true,
      emailCategories: ['success', 'notification', 'error', 'admin', 'error_invoice'],
    };
  },
  async created() {
    this.service = ServiceFactory.getService('CustomerService');
    this.debtorService = ServiceFactory.getService('DebtorService');

    this.service.addEventListener('customersLoaded', async (event) => {
      this.customers = event.detail.data;
      
      // Selecteer klant op basis van URL query parameters (indien aanwezig)
      await this.checkAndSelectCustomerFromUrl();
    });
    this.service.addEventListener('getCustomersFailed', (error) => {
      this.$emit('showToast', [error.detail.error, 'bg-danger']) 
    });
    this.service.addEventListener('createCustomerFailed', (error) => {
      this.$emit('showToast', [error.detail.error, 'bg-danger']) 
    });
    this.service.addEventListener('updateCustomerFailed', async (error) => {
      console.log('updateCustomerFailed', error.detail.error)
      this.$emit('showToast', [error.detail.error, 'bg-danger']) 
      await this.loadCustomers();
      this.selectCustomer(this.selectedCustomer)
    });
    this.service.addEventListener('deleteCustomerFailed', async (error) => {
      this.$emit('showToast', [error.detail.error, 'bg-danger'])
      await this.loadCustomers()
    });
    await this.loadCustomers();
    this.createNewCustomer(); // Initialize with a new customer form
    this.editingCustomer.email = {
      success: [],
      notification: [], 
      error: [],
      admin: []
    };
  },
  async mounted() {
    await this.loadCustomers();
    if (this.customers.length > 0) {
      setTimeout(() => {
        if (!this.checkAndSelectCustomerFromUrl()) {
          this.selectCustomer(this.customers[0]);
        }
      }, 200);
    }
  },
  methods: {
    // Centrale functie om klant te selecteren op basis van URL parameters
    async checkAndSelectCustomerFromUrl() {
      const customerId = this.$route.query.id;
      
      // Als er een ID in de URL staat, probeer die klant te selecteren
      if (customerId && this.customers.length > 0) {
        const customerToSelect = this.customers.find(c => c.id === parseInt(customerId));
        if (customerToSelect && (!this.selectedCustomer || this.selectedCustomer.id !== customerToSelect.id)) {
          await this.selectCustomer(customerToSelect);
          return true;
        }
      } 
      
      // Als er geen ID in de URL staat EN er is nog geen klant geselecteerd EN er zijn klanten beschikbaar
      // selecteer dan automatisch de eerste klant
      else if (!this.selectedCustomer && this.customers.length > 0) {
        await this.selectCustomer(this.customers[0]);
        return true;
      }
      
      return false;
    },
    
    async loadCustomers() {
      const response = await this.service.getCustomers();
      this.customers = response.data;
    },
    async selectCustomer(customer) {
      if (!customer) return;
      
      try {
        // Haal de volledige klant op met alle details
        const response = await this.service.getCustomer(customer.id);
        this.selectedCustomer = response.data;
        
        // Maak een kopie om te bewerken
        this.editingCustomer = { ...this.selectedCustomer };
        this.editingCustomer.password = ''; // Leeg wachtwoord veld voor veiligheid
        
        // Zorg dat alle email categories bestaan
        if (!this.editingCustomer.email) {
          this.editingCustomer.email = {};
        }
        
        for (const category of this.emailCategories) {
          if (!this.editingCustomer.email[category]) {
            this.editingCustomer.email[category] = [];
          }
        }

        // Laad debiteuren voor deze klant
        await this.loadCustomerDebtors(customer.id);
        
        // Werk de URL bij met de geselecteerde klant ID
        // Dit zorgt ervoor dat bij vernieuwen of na opslaan dezelfde klant geselecteerd blijft
        this.$router.push({ 
          query: { ...this.$route.query, id: customer.id.toString() } 
        }).catch(err => {
          // Negeer navigatie-duplicatie fouten
          if (err.name !== 'NavigationDuplicated') {
            throw err;
          }
        });
        
        this.isCreating = false;
      } catch (error) {
        console.error('Error loading customer details:', error);
        this.$emit('showToast', ['Fout bij het laden van klantgegevens', 'bg-danger']);
      }
    },
    async loadCustomerDebtors(customerId) {
      if (!customerId) {
        this.customerDebtors = [];
        return;
      }
      try {
        const response = await this.debtorService.getDebtorsByCustomerId(customerId);
        this.customerDebtors = response.data || [];
      } catch (error) {
        console.error('Fout bij het ophalen van debiteuren:', error);
        this.$emit('showToast', ['Fout bij het ophalen van debiteuren', 'bg-danger']);
      }
    },
    addNewDebtor() {
      if (!this.selectedCustomer || !this.selectedCustomer.id) {
        this.$emit('showToast', ['Selecteer eerst een klant', 'bg-warning']);
        return;
      }
      
      // Navigeer naar de nieuwe debiteurpagina
      this.$router.push(`/customers/${this.selectedCustomer.id}/debtors/new`);
    },
    
    navigateToDebtor(debtorId) {
      this.$router.push(`/customers/${this.selectedCustomer.id}/debtors/${debtorId}`);
    },
    async removeDebtor(id, index) {
      try {
        await this.debtorService.deleteDebtor(id);
        this.customerDebtors.splice(index, 1);
        this.$emit('showToast', ['Debiteur succesvol verwijderd', 'bg-success']);
      } catch (error) {
        console.error('Fout bij het verwijderen van debiteur:', error);
        this.$emit('showToast', ['Fout bij het verwijderen van debiteur', 'bg-danger']);
      }
    },
    createNewCustomer() {
      this.selectedCustomer = null;
      this.editingCustomer = {
        name: '',
        username: '',
        password: '',
        customer_code: '',
        organization_code: '',
        email: [],
        portal_url: ''
      };
      this.customerDebtors = [];
      this.isCreating = true;
      // Verberg het debiteur formulier bij het aanmaken van een nieuwe klant
      this.showDebtorForm = false;
    },
    async saveCustomer() {
      const customerData = { ...this.editingCustomer };
      if (!this.isCreating && !customerData.password) {
        delete customerData.password;
      }
      try {
        let savedCustomer;
        let response;
        if (this.isCreating) {
          response = await this.service.createCustomer(customerData);
          savedCustomer = response.data
          this.$emit('showToast', ['Klant succesvol aangemaakt', 'bg-success']);
        } else {
          response = await this.service.updateCustomer(customerData.id, customerData);
          savedCustomer = response.data
          this.$emit('showToast', ['Klant succesvol bijgewerkt', 'bg-success']);
        }
        console.log('savedCustomer', savedCustomer);
        await this.loadCustomers();
        this.selectCustomer(this.customers.find(c => c.id === savedCustomer.id));
        
        // Update debtors if needed
        for (const debtor of this.customerDebtors) {
          if (debtor.id) {
            await this.debtorService.updateDebtor(debtor.id, debtor);
          }
        }
      } catch (error) {
        this.$emit('showToast', [error.response?.data?.error || error.message, 'bg-danger']);
      }
    },
    async deleteCustomer() {
      if (confirm(`Weet je zeker dat je ${this.selectedCustomer.name} wilt verwijderen?`)) {
        try {
          await this.service.deleteCustomer(this.selectedCustomer.id);
          this.$emit('showToast', ['Klant succesvol verwijderd', 'bg-success']);
          await this.loadCustomers();
          this.createNewCustomer();
        } catch (error) {
          this.$emit('showToast', [error.message, 'bg-danger']);
        }
      }
    },
    updatePassword(newPassword) {
      this.editingCustomer.password = newPassword;
    },
    startEditingDescription() {
      // Sla huidige waarde op voor het geval we willen annuleren
      this.originalDescriptionText = this.editingCustomer.debtor_codes_descr || '';
      this.isEditingDescription = true;
    },
    stopEditingDescription() {
      // Bevestig de wijziging
      this.isEditingDescription = false;
    },
    cancelEditingDescription() {
      // Herstel de originele tekst en sluit bewerkingsmodus
      this.editingCustomer.debtor_codes_descr = this.originalDescriptionText;
      this.isEditingDescription = false;
    },
    // Nieuwe event handlers voor EmailManager component
    async handleEmailSave(event) {
      try {
        await this.service.updateCustomer(this.editingCustomer.id, this.editingCustomer);
        this.$emit('showToast', [
          event.isUpdate ? 'E-mail succesvol bijgewerkt' : 'E-mail succesvol toegevoegd', 
          'bg-success'
        ]);
        await this.saveCustomer();
      } catch (error) {
        console.log('Email save error', error);
        const messageList = [
          'Er is een fout opgetreden bij het opslaan van de e-mail.',
          error.response?.data?.error || '',
        ];
        this.$emit('showToast', [messageList.join(' '), 'bg-danger']);
      }
    },
    async handleEmailDelete() {
      try {
        await this.service.updateCustomer(this.editingCustomer.id, this.editingCustomer);
        this.$emit('showToast', ['E-mail succesvol verwijderd', 'bg-success']);
        await this.saveCustomer();
      } catch (error) {
        this.$emit('showToast', ['Er is een fout opgetreden bij het verwijderen van de e-mail', 'bg-danger']);
      }
    },
    handleEmailError(message) {
      this.$emit('showToast', [message, 'bg-danger']);
    },
    toggleEmailCollapse() {
      this.isEmailCollapsed = !this.isEmailCollapsed;
      localStorage.setItem('isEmailCollapsed', this.isEmailCollapsed);
    },
    toggleDebtorCollapse() {
      this.isDebtorCollapsed = !this.isDebtorCollapsed;
      localStorage.setItem('isDebtorCollapsed', this.isDebtorCollapsed);
    },
    isValidUrl(string) {
      if (!string) return false;
      try {
        const url = new URL(string);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch (_) {
        return false;
      }
    },
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.custom-active {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
  color: white !important;
}

.list-group-item.custom-active:hover {
  background-color: var(--primary) !important;
}

.customer-item {
  cursor: pointer;
}

.customer-item:hover {
  background-color: var(--light);
}

.description-field {
  font-size: 0.7em;
  background-color:#ffffff;
  border-color: #999;
}

.description-field:focus {
  background-color: #fff;
  border-color: #999;
}

.remove-button-col {
  width: 100px; 
}

.badge-wrapper {
  display: flex;
  align-items: center;
}

.badge-container {
  border: 1px solid #ccc;
  padding: 5px;
  min-height: 40px;
  border-radius: 4px;
  flex-grow: 1;
}

.badge-placeholder {
  color: #999; 
  font-style: normal;
}

.badge {
  margin-right: 0.5rem;
  cursor: pointer;
}

.badge-sm {
  font-size: 0.5em;
  padding: 0.2em 0.4em;
  transform: translateY(-0.5em);
  display: inline-block;
  position: relative;
  top: -0.2em;
  vertical-align: super;
}

.btn {
  margin-left: 10px;
}

.border-red {
  border-color: #C7066E !important;
}

.border-black {
  border-color: #000000 !important;
}
</style>
